import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// This file will be renamed to LineChartCanvas.tsx after merge
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Area, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, } from 'recharts';
import { format } from 'date-fns';
import { getGridLinesColor } from '../../../components/charts/options';
import { isDarkScheme } from '../../../utils/colorScheme';
import { IS_APP_CODEAC } from '../../../constants/env';
const MAX_MESSAGE_LENGTH = 40;
export const generateTicks = (min, max, step) => {
    return Array.from({ length: Math.floor((max - min) / step) + 1 }, (_, i) => min + i * step);
};
const getLines = (chartType) => {
    if (chartType === 'codeCoverage') {
        return ['lines', 'linesThreshold', 'functions', 'functionsThreshold', 'branches', 'branchesThreshold'];
    }
    else if (chartType === 'trendInTime') {
        return ['warnings', 'errors', 'issuesThreshold', 'errorsThreshold', 'approxIssuesThreshold', 'approxErrorsThreshold'];
    }
    return [];
};
const getColors = () => ({
    approxErrorsThreshold: 'rgba(135, 9, 6, .4)',
    approxIssuesThreshold: 'rgba(249, 142, 11, .5)',
    branches: isDarkScheme() ? '#3899db' : '#326b9d',
    branchesThreshold: isDarkScheme() ? '#0057a5' : '#92c4f8',
    errors: '#e74c3c',
    errorsThreshold: '#870906',
    functions: '#f1c40f',
    functionsThreshold: '#f4d03f',
    issuesThreshold: '#f98e0b',
    lines: '#4caf50',
    linesThreshold: '#d4edda',
    warnings: '#f1c40f',
});
export function BaseLineChart({ height = 300, chartData, yTicks, chartType, tooltipData, showLegend = false, shortDate = false, handleClick }) {
    const { t } = useTranslation();
    const colors = getColors();
    const formatTooltipLabel = (value, name, props) => {
        if (chartType === 'codeCoverage') {
            let diff = 0;
            // eslint-disable-next-line react/prop-types
            if (props.payload?.id) {
                diff =
                    name === t('codeCoverage.lines')
                        // eslint-disable-next-line react/prop-types
                        ? tooltipData[props.payload.id]?.digestDiff?.getLines()
                        : name === t('codeCoverage.functions')
                            // eslint-disable-next-line react/prop-types
                            ? tooltipData[props.payload.id]?.digestDiff?.getFunctions()
                            // eslint-disable-next-line react/prop-types
                            : tooltipData[props.payload.id]?.digestDiff?.getBranches();
            }
            let tooltip = `${name}: ${Math.round(100 * value) / 100} %`;
            if (diff && Math.abs(diff) > 0.0001) {
                tooltip += ` (${diff > 0 ? '+' : '-'} ${Math.abs(Math.round(100 * diff) / 100)} %)`;
            }
            return [
                _jsxs("div", { children: [_jsx("span", { style: {
                                display: 'inline-block',
                                width: '10px',
                                height: '10px',
                                // eslint-disable-next-line react/prop-types
                                backgroundColor: props.color,
                                marginRight: '5px',
                            } }), tooltip] }, `${name}-wrapper`),
            ];
        }
        else if (chartType === 'trendInTime') {
            // eslint-disable-next-line react/prop-types
            if ((name === 'Approx issues threshold' && props.payload?.chartLines?.issuesThreshold) || (name === 'Approx errors threshold' && props.payload?.chartLines?.errorsThreshold)) {
                return [];
            }
            // eslint-disable-next-line react/prop-types
            const normalizedWarnings = props.payload?.chartLines?.warnings - props.payload?.chartLines.errors;
            // eslint-disable-next-line react/prop-types
            const tooltip = `${name}: ${name === 'Warnings' ? normalizedWarnings : (value ?? 0)}`;
            return [
                _jsxs("div", { children: [_jsx("span", { style: {
                                display: 'inline-block',
                                width: '10px',
                                height: '10px',
                                // eslint-disable-next-line react/prop-types
                                backgroundColor: props.color,
                                marginRight: '5px',
                            } }, name), tooltip] }, `${name}-wrapper`),
            ];
        }
        return value;
    };
    const gradientArray = IS_APP_CODEAC ? ['errors', 'warnings'] : [];
    return (_jsx(ResponsiveContainer, { width: "100%", height: height, children: _jsxs(ComposedChart, { data: chartData, margin: { top: 10, right: 30, left: -30, bottom: 5 }, onClick: handleClick, children: [_jsx("defs", { children: gradientArray.map((key) => (_jsxs("linearGradient", { id: `gradient-${key}`, x1: "0", y1: "0", x2: "0", y2: "1", children: [_jsx("stop", { offset: "5%", stopColor: colors[key], stopOpacity: 0.8 }), _jsx("stop", { offset: "95%", stopColor: colors[key], stopOpacity: 0 })] }, key))) }), _jsx(CartesianGrid, { stroke: getGridLinesColor(), syncWithTicks: true }), _jsx(XAxis, { dataKey: "dateTime", interval: chartData.length > 10 ? 2 : 'preserveStartEnd', tick: { fontSize: 11 }, minTickGap: 5, tickFormatter: (value) => shortDate ? format(new Date(value), 'MM/dd') : value, padding: { left: 0, right: 0 } }), _jsx(YAxis, { ticks: yTicks, tickFormatter: (value) => chartType === 'codeCoverage' ? `${value} %` : value, tick: { fontSize: 11 }, interval: 0 }), _jsx(Tooltip, { cursor: false, formatter: (value, name, props) => formatTooltipLabel(value, name, props), itemStyle: {
                        color: 'white',
                        paddingBottom: 0,
                        paddingTop: 0,
                    }, labelStyle: {
                        fontWeight: 'bold',
                        paddingBottom: '5px',
                    }, labelFormatter: (label) => {
                        const commit = chartData.find(({ dateTime }) => dateTime === label);
                        if (commit?.message) {
                            return `${commit.dateTime}\n${commit.message.length > MAX_MESSAGE_LENGTH
                                ? `${commit.message.substring(0, MAX_MESSAGE_LENGTH)}…`
                                : commit.message}`;
                        }
                    }, contentStyle: {
                        fontSize: '10px',
                        backgroundColor: '#212529',
                        color: 'white',
                        whiteSpace: 'pre-line',
                        borderRadius: '0.25rem',
                    } }), showLegend && _jsx(Legend, { verticalAlign: "top", align: "right", wrapperStyle: { paddingBottom: '10px' }, iconType: "circle", formatter: (value) => {
                        return (_jsx("span", { style: { color: '#777' }, children: value }));
                    }, iconSize: 7 }), getLines(chartType).map((key) => {
                    const lineColor = colors[key];
                    const isThreshold = key.endsWith('Threshold');
                    const isDotted = key === 'approxIssuesThreshold' || key === 'approxErrorsThreshold';
                    const isCoverageThreshold = key === 'linesThreshold' || key === 'functionsThreshold' || key === 'branchesThreshold';
                    if (gradientArray.includes(key)) {
                        return (_jsx(Area, { type: "monotone", dataKey: `chartLines.${key}`, stroke: lineColor, name: t(`graphTitles.${key}`), strokeWidth: 2, fill: `url(#gradient-${key})`, fillOpacity: 1, activeDot: { fill: lineColor, strokeWidth: 0 }, dot: { fill: lineColor, r: 1 } }, key));
                    }
                    return (isDotted ?
                        _jsx(Line, { type: "monotone", dataKey: `chartLines.${key}`, stroke: lineColor, name: t(chartType === 'trendInTime' ? `graphTitles.${key}` : `codeCoverage.${key}`), connectNulls: true, dot: { fill: lineColor, r: 0 }, 
                            // eslint-disable-next-line no-undefined
                            strokeDasharray: "4 4", activeDot: { fill: lineColor, strokeWidth: 0 } }, key) :
                        _jsx(Line, { type: "monotone", dataKey: `chartLines.${key}`, stroke: lineColor, name: t(chartType === 'trendInTime' ? `graphTitles.${key}` : `codeCoverage.${key}`), onClick: (data, index) => {
                                handleClick?.(data);
                            }, strokeWidth: isCoverageThreshold ? 1 : 2, connectNulls: true, dot: { fill: lineColor, r: isThreshold ? 0.1 : 1 }, activeDot: { fill: lineColor, strokeWidth: 0 } }, key));
                })] }) }));
}
